import { useRef, useState } from 'react';
import { Camera, Link2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { apiFetch } from '../lib/apiClient';

/**
 * Photo picker: upload/take PNG/JPG (mobile camera via capture) or paste a URL.
 * Calls onChange with the resulting public photo_url.
 */
export default function ImagePickerField({
  label = 'Photo',
  value = '',
  onChange,
  testId = 'image-picker',
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(Boolean(value && !value.includes('/storage/v1/')));

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const type = (file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(type)) {
      toast.error('Only PNG or JPG images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller');
      return;
    }

    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await apiFetch('/api/uploads/image', {
        method: 'POST',
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : '';
        if (response.status === 503 || /not configured/i.test(detail)) {
          throw new Error(
            'Photo uploads are not configured on the server yet. Ask an admin to set SUPABASE_SERVICE_ROLE_KEY and create the vendor-images bucket.'
          );
        }
        throw new Error(detail || 'Upload failed');
      }
      onChange(data.url || '');
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {value ? (
        <img
          src={value}
          alt="Preview"
          className="w-full h-40 object-cover rounded-lg border"
        />
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testId}-file`}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testId}-upload`}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Camera className="w-4 h-4 mr-2" />
          )}
          {uploading ? 'Uploading…' : 'Upload / Take photo'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowUrl((v) => !v)}
        >
          <Link2 className="w-4 h-4 mr-1" />
          {showUrl ? 'Hide URL' : 'Paste URL'}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {showUrl ? (
        <Input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…/.jpg or .png"
          data-testid={`${testId}-url`}
        />
      ) : null}
    </div>
  );
}
